import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
    Box, Paper, Typography, TextField, Button, Alert, InputAdornment,
    IconButton,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HomeIcon from '@mui/icons-material/Home';
import useAuth from '../../hooks/useAuth';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login, getHomePath } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirect = searchParams.get('redirect');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const user = await login(email, password);
            navigate(redirect || getHomePath(user.role));
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur de connexion');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #1a5276 0%, #2e86c1 50%, #3498db 100%)',
            p: 2,
        }}>
            <Paper sx={{ maxWidth: 440, width: '100%', p: 4, borderRadius: 3 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h4" fontWeight={700} color="primary">
                        OFPPT
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                        Connectez-vous à votre compte
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth label="Email" type="email" value={email}
                        onChange={(e) => setEmail(e.target.value)} required
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment>,
                        }}
                    />
                    <TextField
                        fullWidth label="Mot de passe"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)} required
                        sx={{ mb: 3 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><LockIcon color="action" /></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        type="submit" fullWidth variant="contained" size="large"
                        disabled={loading}
                        sx={{
                            background: 'linear-gradient(135deg, #1a5276, #2e86c1)',
                            textTransform: 'none', borderRadius: 2, py: 1.5, mb: 2,
                        }}
                    >
                        {loading ? 'Connexion...' : 'Se connecter'}
                    </Button>
                </form>

                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Pas encore de compte ?{' '}
                        <Link to="/register" style={{ color: '#2e86c1', fontWeight: 600 }}>
                            Créer un compte
                        </Link>
                    </Typography>
                    <Button
                        component={Link} to="/"
                        startIcon={<HomeIcon />}
                        sx={{ mt: 2, textTransform: 'none', color: 'text.secondary' }}
                    >
                        Retour aux offres
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
